import React, { useState, useRef } from 'react';
import { intendedRequest, releaseRejectedRequest } from '../../utils/intendedRequest';

// Mission 02 / Step 15: "Enregistrer un paiement" - composant réutilisable
// (Commandes/Ventes, CRM, Créances). Le reste à payer affiché après succès
// vient TOUJOURS de la réponse serveur (addSalePayment), jamais recalculé
// de manière optimiste côté client.
const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Espèces' },
  { value: 'ORANGE_MONEY', label: 'Orange Money' },
  { value: 'WAVE', label: 'Wave' },
  { value: 'BANK_TRANSFER', label: 'Virement' },
  { value: 'OTHER', label: 'Autre' }
];

const money = (value) => `${Number(value || 0).toLocaleString('fr-FR')} F CFA`;

export default function PaymentModal({ sale, onClose, onSubmit }) {
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const paymentRequest = useRef(null);
  const inFlight = useRef(false);

  const remaining = Number(sale.remaining);

  const submit = async () => {
    if (inFlight.current || result) return;
    const value = Number(amount);
    if (!(value > 0)) { setError('Montant invalide.'); return; }
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    try {
      const res = await onSubmit(intendedRequest(paymentRequest, { amount: value, paymentMethod, note: note.trim() || undefined }));
      setResult(res);
    } catch (err) {
      releaseRejectedRequest(paymentRequest, err);
      if (err?.code === 'OVERPAYMENT') setError('Ce montant dépasse le reste à payer.');
      else if (err?.code === 'SALE_CANCELLED') setError('Cette vente est annulée: paiement impossible.');
      else setError(err?.message || 'Paiement impossible.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={() => { if (!inFlight.current) onClose(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {result ? (
          <>
            <h3 style={{ marginTop: 0 }}>Paiement enregistré</h3>
            {Number(result.remaining) > 0 ? (
              <p style={{ fontSize: 16 }}>Reste à payer : <strong>{money(result.remaining)}</strong></p>
            ) : (
              <p style={{ color: '#1B6B4A', fontWeight: 700, fontSize: 16 }}>Dette entièrement réglée</p>
            )}
            <button onClick={onClose} style={primaryBtn}>Fermer</button>
          </>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>Enregistrer un paiement</h3>
            {sale.customerName && <p style={{ color: '#64748b', margin: '0 0 2px' }}>{sale.customerName}</p>}
            {sale.saleNumber && <p style={{ color: '#64748b', margin: '0 0 12px' }}>Vente {sale.saleNumber}</p>}
            <p style={{ margin: '0 0 16px', fontSize: 15 }}>Reste à payer : <strong>{money(remaining)}</strong></p>

            <label style={labelStyle}>Montant reçu</label>
            <input
              type="number" min={1} max={remaining} value={amount}
              onChange={(e) => setAmount(e.target.value)} style={inputStyle} autoFocus
            />

            <label style={labelStyle}>Mode de paiement</label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={inputStyle}>
              {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>

            <label style={labelStyle}>Note (optionnel)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} />

            {error && <div style={errorStyle}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} style={outlineBtn} disabled={submitting}>Annuler</button>
              <button onClick={submit} style={primaryBtn} disabled={submitting}>
                {submitting ? 'Enregistrement...' : 'Enregistrer le paiement'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,23,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
};

const modalStyle = {
  background: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420,
  boxShadow: '0 12px 32px rgba(0,0,0,0.2)'
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', margin: '12px 0 6px' };

const inputStyle = {
  width: '100%', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', boxSizing: 'border-box', fontSize: 15
};

const primaryBtn = {
  flex: 1, border: 'none', borderRadius: 10, padding: '12px 16px', background: '#1B6B4A',
  color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 15
};

const outlineBtn = {
  flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', background: 'white',
  color: '#334155', fontWeight: 700, cursor: 'pointer', fontSize: 15
};

const errorStyle = {
  marginTop: 12, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 10, fontSize: 14
};
